import { useEffect, useRef, useState } from "react";
import { workerTimers } from "react-idle-timer";
import useMsalB2C from "hooks/useMsalB2C";
import { clearAllCookies } from "utils/cookieUtils";
import {
  getJwtRemainingSeconds,
  describeStoredJwt,
  formatDuration,
  sessionDebugLog,
} from "utils/sessionExpirationUtils";

export const SESSION_WARNING_SECONDS = 300;
const MILLISECONDS_PER_SECOND = 1000; // 1,000 ms = 1 second.
const SESSION_CHECK_INTERVAL_MS = 15000; // 15,000 ms = 15 seconds.
const SESSION_RETRY_INTERVAL_MS = 1000; // 1,000 ms = 1 second.
const MAX_SESSION_RETRIES = 10;

interface UseSessionExpirationOptions {
  isImpersonation: boolean;
  onImpersonationExpired: () => void;
}

interface UseSessionExpirationResult {
  isTokenExpired: boolean;
  isTokenWarningVisible: boolean;
  isRefreshingToken: boolean;
  tokenSecondsRemaining: number;
  endSession: () => void;
  refreshToken: () => Promise<void>;
}

/**
 * Supervisa la expiración del JWT, permite renovarlo y finaliza la sesión cuando procede.
 * En impersonación conserva la sesión MSAL y muestra únicamente el aviso informativo.
 */
const useSessionExpiration = ({
  isImpersonation,
  onImpersonationExpired,
}: UseSessionExpirationOptions): UseSessionExpirationResult => {
  const { msalInstance } = useMsalB2C();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isTokenWarningVisible, setIsTokenWarningVisible] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const [tokenSecondsRemaining, setTokenSecondsRemaining] = useState(
    SESSION_WARNING_SECONDS,
  );

  const logoutInProgressRef = useRef(false);
  const refreshInProgressRef = useRef(false);
  const sessionEstablishedRef = useRef(false);
  const warningTimeoutRef = useRef<number | null>(null);
  const expirationTimeoutRef = useRef<number | null>(null);
  const warningIntervalRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);

  /** Cancela todos los temporizadores relacionados con aviso, expiración y reintentos. */
  const clearSessionTimers = () => {
    [warningTimeoutRef, expirationTimeoutRef, retryTimeoutRef].forEach(
      (timerRef) => {
        if (timerRef.current !== null) {
          workerTimers.clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      },
    );
    if (warningIntervalRef.current !== null) {
      workerTimers.clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  };

  /** Comprueba si existe un token de API o alguna cuenta registrada en MSAL. */
  const hasSession = () => {
    if (localStorage.getItem("token")) return true;
    if (msalInstance.getActiveAccount()) return true;
    return msalInstance.getAllAccounts().length > 0;
  };

  /** Obtiene los segundos restantes priorizando el mismo JWT que consumen las APIs. */
  const getSecondsUntilExpiration = () => {
    const apiTokenRemaining = getJwtRemainingSeconds(
      localStorage.getItem("token"),
    );
    if (apiTokenRemaining !== null) return apiTokenRemaining;

    const account = msalInstance.getActiveAccount();
    const expiration = account?.idTokenClaims?.exp;
    return typeof expiration === "number"
      ? expiration - Math.floor(Date.now() / 1000)
      : null;
  };

  /** Limpia los datos locales y solicita a MSAL el cierre completo de la sesión. */
  const logout = async () => {
    if (logoutInProgressRef.current || isImpersonation) {
      sessionDebugLog("logout skipped", {
        logoutInProgress: logoutInProgressRef.current,
        isImpersonation,
      });
      return;
    }
    logoutInProgressRef.current = true;
    sessionDebugLog("logout: clearing storage and redirecting");

    try {
      clearAllCookies();
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Error clearing storage on logout:", error);
    }
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}/inicio`,
      });
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/inicio");
    }
  };

  /** Aplica el final de sesión adecuado para una sesión normal o una impersonación. */
  const endSession = () => {
    sessionDebugLog("endSession", { isImpersonation });
    clearSessionTimers();

    if (isImpersonation) {
      setIsTokenExpired(true);
      setIsTokenWarningVisible(false);
      onImpersonationExpired();
      return;
    }

    void logout();
  };

  /** Muestra el aviso previo y mantiene actualizada su cuenta atrás visible. */
  const showExpirationWarning = (secondsRemaining: number) => {
    sessionDebugLog("token warning shown", {
      remaining: formatDuration(secondsRemaining * MILLISECONDS_PER_SECOND),
    });
    setTokenSecondsRemaining(Math.max(0, Math.ceil(secondsRemaining)));
    setIsTokenWarningVisible(true);

    warningIntervalRef.current = workerTimers.setInterval(() => {
      const remaining = getSecondsUntilExpiration();
      setTokenSecondsRemaining(Math.max(0, Math.ceil(remaining ?? 0)));
    }, MILLISECONDS_PER_SECOND);
  };

  /** Programa el aviso previo y la comprobación exacta de expiración del token. */
  const scheduleExpiration = (secondsRemaining: number) => {
    clearSessionTimers();

    // Equivalent to secondsRemaining seconds expressed in milliseconds.
    const expirationDelayMs = secondsRemaining * MILLISECONDS_PER_SECOND;
    expirationTimeoutRef.current = workerTimers.setTimeout(
      checkExpiration,
      expirationDelayMs,
    );

    if (isImpersonation) return;

    if (secondsRemaining <= SESSION_WARNING_SECONDS) {
      showExpirationWarning(secondsRemaining);
      return;
    }

    // Equivalent to secondsRemaining minus 300 seconds, expressed in milliseconds.
    const warningDelayMs =
      (secondsRemaining - SESSION_WARNING_SECONDS) * MILLISECONDS_PER_SECOND;
    warningTimeoutRef.current = workerTimers.setTimeout(
      checkExpiration,
      warningDelayMs,
    );
  };

  /** Comprueba la expiración y reintenta temporalmente si MSAL aún no está preparado. */
  function checkExpiration(retry = 0, source = "timer") {
    if (refreshInProgressRef.current) return;

    const sessionExists = hasSession();
    if (!sessionEstablishedRef.current && !sessionExists) {
      sessionDebugLog(`checkExpiration (${source}): no session yet`);
      return;
    }
    sessionEstablishedRef.current ||= sessionExists;

    const secondsRemaining = getSecondsUntilExpiration();
    sessionDebugLog(`checkExpiration (${source})`, {
      remaining: formatDuration(
        secondsRemaining === null
          ? null
          : secondsRemaining * MILLISECONDS_PER_SECOND,
      ),
      retry,
      token: describeStoredJwt(localStorage.getItem("token")),
    });
    if (secondsRemaining === null) {
      if (retry < MAX_SESSION_RETRIES) {
        retryTimeoutRef.current = workerTimers.setTimeout(
          () => checkExpiration(retry + 1, "retry"),
          SESSION_RETRY_INTERVAL_MS,
        );
      } else {
        endSession();
      }
      return;
    }

    if (secondsRemaining <= 0) {
      endSession();
      return;
    }

    scheduleExpiration(secondsRemaining);
  }

  /** Renueva el token bajo petición del usuario y reprograma su ciclo de expiración. */
  const refreshToken = async () => {
    if (refreshInProgressRef.current) return;

    refreshInProgressRef.current = true;
    setIsRefreshingToken(true);
    clearSessionTimers();

    try {
      const account = msalInstance.getActiveAccount();
      if (!account) throw new Error("No active account available");

      const result = await msalInstance.acquireTokenSilent({
        account,
        scopes: ["openid", "profile"],
        forceRefresh: true,
      });
      if (!result.idToken) throw new Error("MSAL did not return an ID token");

      localStorage.setItem("token", JSON.stringify(result.idToken));
      setIsTokenExpired(false);
      setIsTokenWarningVisible(false);
      setTokenSecondsRemaining(SESSION_WARNING_SECONDS);
      refreshInProgressRef.current = false;
      sessionDebugLog("token refreshed");
      checkExpiration(0, "refresh");
    } catch (error) {
      console.error("Error al refrescar el token:", error);
      await logout();
    } finally {
      refreshInProgressRef.current = false;
      setIsRefreshingToken(false);
    }
  };

  // Comprueba periódicamente y al recuperar la página para cubrir temporizadores suspendidos.
  useEffect(() => {
    checkExpiration(0, "mount");

    const checkOnInterval = () => checkExpiration(0, "interval");
    const handleWindowFocus = (_event: FocusEvent) =>
      checkExpiration(0, "focus");
    const handlePageShow = (_event: PageTransitionEvent) =>
      checkExpiration(0, "pageshow");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible")
        checkExpiration(0, "visibility");
    };
    const safetyCheck = workerTimers.setInterval(
      checkOnInterval,
      SESSION_CHECK_INTERVAL_MS,
    );
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      workerTimers.clearInterval(safetyCheck);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearSessionTimers();
    };
  }, []);

  return {
    isTokenExpired,
    isTokenWarningVisible,
    isRefreshingToken,
    tokenSecondsRemaining,
    endSession,
    refreshToken,
  };
};

export default useSessionExpiration;
